import { useState, type ReactNode } from "react";

import type { FormatTag, ThemeTag, TopicTag } from "@/lib/constants";
import { theme } from "@/lib/theme";

interface FilterButtonsProps {
  selectedFormat: FormatTag | null;
  selectedThemes?: ThemeTag[];
  selectedTopics?: TopicTag[];
  onFormatChange: (format: FormatTag) => void;
  onThemeChange?: (theme: ThemeTag) => void;
  onTopicChange?: (topic: TopicTag) => void;
  formatTags?: string[];
  themeTags?: string[];
  topicTags?: string[];
  showOnlyFormat?: boolean;
}

export default function FilterButtons({
  selectedFormat,
  selectedThemes = [],
  selectedTopics = [],
  onFormatChange,
  onThemeChange,
  onTopicChange,
  formatTags = [],
  themeTags = [],
  topicTags = [],
  showOnlyFormat = false,
}: FilterButtonsProps) {
  const [isThemesOpen, setIsThemesOpen] = useState(false);
  const [isTopicsOpen, setIsTopicsOpen] = useState(false);

  const FilterButton = ({
    onClick,
    isSelected,
    children,
  }: {
    onClick: () => void;
    isSelected: boolean;
    children: ReactNode;
  }) => (
    <button onClick={onClick} className="relative">
      <div className="absolute inset-0 bg-gray-900 dark:bg-gray-700 rounded-lg translate-y-1 translate-x-1" />
      <div
        className={`relative z-10 px-4 py-2 rounded-lg border-2 border-gray-900 dark:border-gray-700 font-medium transition-all duration-200 ${
          isSelected
            ? "bg-gray-900 dark:bg-gray-700 text-white translate-y-1 translate-x-1"
            : `${theme.colors.cardCream} dark:bg-gray-800 text-gray-900 dark:text-white hover:-translate-y-0.5 hover:-translate-x-0.5`
        }`}
      >
        {children}
      </div>
    </button>
  );

  if (showOnlyFormat) {
    return (
      <div className="space-y-8">
        <section>
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Format</h2>
          <div className="flex flex-wrap gap-4">
            {formatTags.map(format => (
              <FilterButton key={format} onClick={() => onFormatChange(format as FormatTag)} isSelected={selectedFormat === format}>
                {format}
              </FilterButton>
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {onThemeChange && (
        <section>
          <button
            onClick={() => setIsThemesOpen(prev => !prev)}
            className="w-full flex items-center justify-between text-xl font-bold mb-4 text-gray-900 dark:text-white hover:opacity-75"
          >
            <span>Themes</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-6 w-6 transform transition-transform ${isThemesOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className="space-y-4">
            {!isThemesOpen && selectedThemes.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {themeTags
                  .filter(theme => selectedThemes.includes(theme as ThemeTag))
                  .map(theme => (
                    <FilterButton key={theme} onClick={() => onThemeChange(theme as ThemeTag)} isSelected>
                      {theme}
                    </FilterButton>
                  ))}
              </div>
            )}

            {isThemesOpen && (
              <div className="flex flex-wrap gap-4">
                {themeTags.map(theme => (
                  <FilterButton
                    key={theme}
                    onClick={() => onThemeChange(theme as ThemeTag)}
                    isSelected={selectedThemes.includes(theme as ThemeTag)}
                  >
                    {theme}
                  </FilterButton>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {onTopicChange && (
        <section>
          <button
            onClick={() => setIsTopicsOpen(prev => !prev)}
            className="w-full flex items-center justify-between text-xl font-bold mb-4 text-gray-900 dark:text-white hover:opacity-75"
          >
            <span>Topics</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-6 w-6 transform transition-transform ${isTopicsOpen ? "rotate-180" : ""}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <div className="space-y-4">
            {!isTopicsOpen && selectedTopics.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {topicTags
                  .filter(topic => selectedTopics.includes(topic as TopicTag))
                  .map(topic => (
                    <FilterButton key={topic} onClick={() => onTopicChange(topic as TopicTag)} isSelected>
                      {topic}
                    </FilterButton>
                  ))}
              </div>
            )}

            {isTopicsOpen && (
              <div className="flex flex-wrap gap-4">
                {topicTags.map(topic => (
                  <FilterButton
                    key={topic}
                    onClick={() => onTopicChange(topic as TopicTag)}
                    isSelected={selectedTopics.includes(topic as TopicTag)}
                  >
                    {topic}
                  </FilterButton>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
